import { DbgPaths } from "../../../../../utils/Paths"
import React from "react"
import { FormattedMessage } from "react-intl"
import { Link, Typography, UnorderedList, UnorderedListItem } from "@carrotfertility/carotene-core"
import { useSelector } from "react-redux"
import { getSubsidies } from "#/redux/reducers/subsidies"
import { CategoryCoverage, getCategoryCoverage } from "../../../../../utils/CategoryCoverage"
import useBenefit from "../../../../dynamic-content/hooks/useGetBenefit"
import useCompanyComplianceConfigLineItemFilter from "../../hooks/useGetCompanyComplianceConfigLineItemFilter"
import { CategorySections } from "../PayingForCareModal"
import useGetMemberCountry from "#/components/company/hooks/useGetMemberCountry"
import { PiiText } from "#/services/tracking"

const OtherExpensesHeader = (): JSX.Element => {
  const coverage = getCategoryCoverage(useSelector(getSubsidies))
  // This is a hack to check if there are any bullets to display - we should find a better approach in the future
  const coverageBulletList = <CoverageBulletList coverage={coverage} />
  const hasBullets = coverageBulletList !== null

  if (coverage.medication) {
    return (
      <p>
        <FormattedMessage
          defaultMessage="This category includes all <link>eligible expenses</link> except medications."
          values={{
            link: (linkContent) => (
              <Link target="_blank" href={DbgPaths.CARROT_ELIGIBLE}>
                {linkContent}
              </Link>
            )
          }}
        />
      </p>
    )
  }
  if (hasBullets) {
    if (coverage.qme) {
      if (coverage.hasAnyNonQme()) {
        return (
          <p>
            <FormattedMessage defaultMessage="This category includes eligible expenses that are:" />
            <UnorderedList>
              <UnorderedListItem>
                <FormattedMessage
                  defaultMessage="Not <link>qualified medical expenses</link>"
                  values={{
                    link: (linkContent) => (
                      <Link target="_blank" href={DbgPaths.QUALIFIED_MEDICAL_EXPENSES}>
                        {linkContent}
                      </Link>
                    )
                  }}
                />
              </UnorderedListItem>
              <UnorderedListItem>
                <FormattedMessage
                  defaultMessage="<bold>And</bold> not covered by another category"
                  values={{
                    bold: (boldContent) => (
                      <Typography component={PiiText} fontWeight="bold">
                        {boldContent}
                      </Typography>
                    )
                  }}
                />
              </UnorderedListItem>
            </UnorderedList>
          </p>
        )
      } else {
        return (
          <p>
            <FormattedMessage
              defaultMessage="This category includes all eligible expenses that are not <link>qualified medical expenses.</link>"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.QUALIFIED_MEDICAL_EXPENSES}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        )
      }
    } else {
      return (
        <p>
          <FormattedMessage defaultMessage="This category includes eligible expenses for the following journeys:" />
        </p>
      )
    }
  } else {
    return (
      <p>
        <FormattedMessage
          defaultMessage="This includes any <link>eligible expenses</link> not covered by another category."
          values={{
            link: (linkContent) => (
              <Link target="_blank" href={DbgPaths.CARROT_ELIGIBLE}>
                {linkContent}
              </Link>
            )
          }}
        />
      </p>
    )
  }
}

const OtherExpensesListItems = (): JSX.Element => {
  const coverage = getCategoryCoverage(useSelector(getSubsidies))
  return <CoverageBulletList coverage={coverage} />
}

function CoverageBulletList({ coverage }: { coverage: CategoryCoverage }): JSX.Element {
  const { data: benefitInfo } = useBenefit()
  const memberCountry = useGetMemberCountry()
  const countryCode = memberCountry?.data?.countryCode
  const { data: lineItemFilter } = useCompanyComplianceConfigLineItemFilter()

  const preservation = benefitInfo.journeys.fertilityPreservation
  const fertilityCare = benefitInfo.journeys.fertilityCare
  const gcCare = benefitInfo.journeys.gestationalCarrier
  const adoptionCare = benefitInfo.journeys.adoption
  const pregnancyCoverage = benefitInfo.journeys.pregnancy
  const hasElectiveFertilityLineItemFilter = lineItemFilter === "ELECTIVE_FERTILITY"
  const menopause = benefitInfo.journeys.menopause
  const lowTestosterone = benefitInfo.journeys.lowTestosterone
  const gacCare = benefitInfo.journeys.genderAffirmingCare
  const partnerEligibility = benefitInfo.partnerEligibility

  const coverageListItems: React.JSX.Element[] = []

  if (coverage.medication) {
    return null
  }

  if (coverage.qme) {
    if (partnerEligibility?.isEligibleForCoverage && !partnerEligibility?.limitations.mustBeLegalPartner) {
      coverageListItems.push(
        <UnorderedListItem key="carrot-eligible-partner">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="Any <link>eligible care and services</link> received by the member’s <link>Carrot-eligible partner</link>, if not legally married or tax-dependent"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.CARROT_ELIGIBLE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (fertilityCare.eligibleExpensesDetail.isElectiveCareCovered && !hasElectiveFertilityLineItemFilter) {
      coverageListItems.push(
        <UnorderedListItem key="fertility-care-no-elective-line-item-filter">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="Eligible <link>fertility care</link> expenses not related to an infertility diagnosis issued by a fertility doctor"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (fertilityCare.eligibleExpensesDetail.hasDonorAssistance) {
      if (hasElectiveFertilityLineItemFilter) {
        coverageListItems.push(
          <UnorderedListItem key="donor-assistance-elective-line-item-filter">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="Eligible <link>donor assistance</link> expenses related to fertility care"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      } else {
        coverageListItems.push(
          <UnorderedListItem key="donor-assistance">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="Eligible <link>donor assistance</link> expenses related to fertility care, with or without an infertility diagnosis"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      }
    }
    if (preservation.eligibleExpensesDetail.isElectivePreservationCovered && !hasElectiveFertilityLineItemFilter) {
      coverageListItems.push(
        <UnorderedListItem key="fertility-preservation">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="Eligible <link>fertility preservation</link> expenses not related to an infertility diagnosis or medical necessity, as determined by a fertility doctor"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.PRESERVATION_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (
      preservation.eligibleExpensesDetail.isElectivePreservationCovered &&
      preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered
    ) {
      if (hasElectiveFertilityLineItemFilter) {
        coverageListItems.push(
          <UnorderedListItem key="fertility-preservation-storage-elective-line-item-filter">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="Eligible <link>fertility preservation</link> storage expenses after the first year"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.PRESERVATION_COVERAGE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      } else {
        coverageListItems.push(
          <UnorderedListItem key="fertility-preservation-storage">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="Eligible <link>fertility preservation</link> storage expenses after the first year, with or without an infertility diagnosis or medical necessity"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.PRESERVATION_COVERAGE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      }
    }
    if (benefitInfo.journeys.adoption.hasEligibleExpenses && !coverage.adoption) {
      coverageListItems.push(
        <UnorderedListItem key="adoption">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="Eligible <link>adoption</link> expenses"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.ADOPTION_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (benefitInfo.journeys.gestationalCarrier.hasEligibleExpenses && !coverage.gc) {
      coverageListItems.push(
        <UnorderedListItem key="gestational-surrogacy">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="Eligible <link>gestational surrogacy</link> expenses, including related donor assistance"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.GESTATIONAL_SURROGACY_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (pregnancyCoverage.hasEligibleExpenses && !coverage.pregnancy) {
      coverageListItems.push(
        <UnorderedListItem key="pregnancy-and-postpartum">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="Eligible <link>pregnancy and postpartum</link> expenses"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.PREGNANCY_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (menopause.hasEligibleExpenses) {
      if (["US", "AU"].includes(countryCode)) {
        coverageListItems.push(
          <UnorderedListItem key="menopause-us-au">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="Eligible Eight Sleep and Perfectly Snug products for <link>menopause</link>"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.MENOPAUSE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      } else if (["GB", "DE"].includes(countryCode)) {
        coverageListItems.push(
          <UnorderedListItem key="menopause-gb-de">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="Eligible Eight Sleep products for <link>menopause</link>"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.MENOPAUSE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      }
    }
  } else {
    if (
      !coverage.preservation &&
      (preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered ||
        preservation.eligibleExpensesDetail.isElectivePreservationCovered)
    ) {
      coverageListItems.push(
        <UnorderedListItem key="fertility-preservation-elective-medically-necessary">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="<link>Fertility preservation</link>"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.PRESERVATION_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (!coverage.fertility) {
      if (
        fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis ||
        fertilityCare.eligibleExpensesDetail.isElectiveCareCovered
      ) {
        if (fertilityCare.eligibleExpensesDetail.hasDonorAssistance) {
          coverageListItems.push(
            <UnorderedListItem key="fertility-care-donor-assistance">
              <p style={{ lineHeight: 1.5 }}>
                <FormattedMessage
                  defaultMessage="<link>Fertility care</link>, including related donor assistance"
                  values={{
                    link: (linkContent) => (
                      <Link target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                        {linkContent}
                      </Link>
                    )
                  }}
                />
              </p>
            </UnorderedListItem>
          )
        } else {
          coverageListItems.push(
            <UnorderedListItem key="fertility-care">
              <p style={{ lineHeight: 1.5 }}>
                <FormattedMessage
                  defaultMessage="<link>Fertility care</link>"
                  values={{
                    link: (linkContent) => (
                      <Link target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                        {linkContent}
                      </Link>
                    )
                  }}
                />
              </p>
            </UnorderedListItem>
          )
        }
      } else if (fertilityCare.eligibleExpensesDetail.hasDonorAssistance) {
        coverageListItems.push(
          <UnorderedListItem key="donor-assistance-fertility-care">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="<link>Donor assistance</link> related to fertility care"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      }
    }
    if (!coverage.gc && gcCare.hasEligibleExpenses) {
      coverageListItems.push(
        <UnorderedListItem key="gestational-surrogacy-no-coverage-eligible-expenses">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="<link>Gestational surrogacy</link>"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.GESTATIONAL_SURROGACY_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (!coverage.adoption && adoptionCare.hasEligibleExpenses) {
      coverageListItems.push(
        <UnorderedListItem key="adoption-no-coverage-eligible-expenses">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="<link>Adoption</link>"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.ADOPTION_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (!coverage.pregnancy && pregnancyCoverage.hasEligibleExpenses) {
      coverageListItems.push(
        <UnorderedListItem key="pregnancy-no-coverage-eligible-expenses">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="<link>Pregnancy and postpartum</link>"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.PREGNANCY_COVERAGE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
    if (!coverage.mlt) {
      if (menopause.hasEligibleExpenses) {
        coverageListItems.push(
          <UnorderedListItem key="menopause-no-coverage-eligible-expenses">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="<link>Menopause</link>"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.MENOPAUSE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      }
      if (lowTestosterone.hasEligibleExpenses) {
        coverageListItems.push(
          <UnorderedListItem key="low-testosterone-no-coverage-eligible-expenses">
            <p style={{ lineHeight: 1.5 }}>
              <FormattedMessage
                defaultMessage="<link>Low testosterone</link>"
                values={{
                  link: (linkContent) => (
                    <Link target="_blank" href={DbgPaths.LOW_TESTOSTERONE}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )
      }
    }
    if (!coverage.gac && gacCare.hasEligibleExpenses) {
      coverageListItems.push(
        <UnorderedListItem key="gender-affirming-care-no-coverage-eligible-expenses">
          <p style={{ lineHeight: 1.5 }}>
            <FormattedMessage
              defaultMessage="<link>Gender-affirming care</link>"
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={DbgPaths.GENDER_AFFIRMING_CARE}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )
    }
  }
  return coverageListItems.length > 0 ? <UnorderedList>{coverageListItems}</UnorderedList> : null
}

export function getOtherExpensesSections(hasQmeCategory: boolean): CategorySections {
  return {
    header: <OtherExpensesHeader />,
    listItems: hasQmeCategory ? null : <OtherExpensesListItems />,
    includeSub: true
  }
}
